import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { useTelegram } from '@/shared/lib/telegram'
import type { ProductType } from '@/shared/lib/product-type'

// ============================================================
// Типы (соответствуют backend/src/modules/favorite/)
// ============================================================

export type { ProductType }

export interface FavoriteProduct {
  id: string
  name: string
  slug: string
  imageUrl: string | null
  priceAmount: number
  salePriceAmount: number | null
  isOnSale: boolean
  isInStock: boolean
}

export interface FavoriteItem {
  id: string
  userId: number
  productType: ProductType
  productId: string
  createdAt: string
  product?: FavoriteProduct
}

interface FavoritesResponse {
  success: boolean
  data: FavoriteItem[]
}

interface FavoriteCountResponse {
  success: boolean
  data: { count: number }
}

interface ToggleFavoriteResponse {
  success: boolean
  data: {
    added: boolean
    item?: FavoriteItem
  }
}

interface CheckFavoriteResponse {
  success: boolean
  data: { inFavorites: boolean }
}

// ============================================================
// API Hooks
// ============================================================

/**
 * Получить список избранного
 */
export function useFavorites() {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['favorites'],
    queryFn: () => apiClient<FavoritesResponse>('/api/user/favorites', { initData }),
    enabled: !!initData,
    staleTime: 30 * 1000,
  })
}

/**
 * Получить количество избранного
 */
export function useFavoritesCount() {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['favorites', 'count'],
    queryFn: () => apiClient<FavoriteCountResponse>('/api/user/favorites/count', { initData }),
    enabled: !!initData,
    staleTime: 30 * 1000,
  })
}

/**
 * Toggle избранного (добавить/удалить)
 */
export function useToggleFavorite() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { productType: ProductType; productId: string }) => {
      if (!initData) throw new Error('Требуется авторизация')
      
      return apiClient<ToggleFavoriteResponse>('/api/user/favorites/toggle', {
        method: 'POST',
        body: JSON.stringify(data),
        initData,
      })
    },
    // Optimistic update
    onMutate: async ({ productId }) => {
      await queryClient.cancelQueries({ queryKey: ['favorites'] })
      const previousFavorites = queryClient.getQueryData<FavoritesResponse>(['favorites'])
      
      // Optimistic toggle
      if (previousFavorites) {
        const isCurrentlyFavorite = previousFavorites.data.some(
          item => item.productId === productId
        )
        
        if (isCurrentlyFavorite) {
          // Удаляем optimistically
          queryClient.setQueryData<FavoritesResponse>(['favorites'], {
            ...previousFavorites,
            data: previousFavorites.data.filter(item => item.productId !== productId),
          })
        }
        // Для добавления не делаем optimistic update — слишком много данных нужно
      }
      
      return { previousFavorites }
    },
    onError: (_err, _vars, context) => {
      if (context?.previousFavorites) {
        queryClient.setQueryData(['favorites'], context.previousFavorites)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
    },
  })
}

/**
 * Проверить, в избранном ли товар
 */
export function useIsFavorite(productType: ProductType, productId: string) {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['favorites', 'check', productType, productId],
    queryFn: () => apiClient<CheckFavoriteResponse>(
      `/api/user/favorites/check/${productType}/${productId}`,
      { initData }
    ),
    enabled: !!initData && !!productId,
    staleTime: 30 * 1000,
  })
}

/**
 * Удалить из избранного
 */
export function useRemoveFromFavorites() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { productType: ProductType; productId: string }) => {
      if (!initData) throw new Error('Требуется авторизация')
      
      return apiClient(`/api/user/favorites/${data.productType}/${data.productId}`, {
        method: 'DELETE',
        initData,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
    },
  })
}

// Re-export для удобства
export type { FavoriteItem as FavoriteItemResponse }
